import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ProgressSpinner } from "primereact/progressspinner";
import { BillingOrderDetailsData, BillingReceiptError } from "../../interfaces/billing-order-details.interface";
import { downloadBillingInvoice, getBillingOrderDetails } from "../../services/billing-order-details/billing-order-details.service";

const money = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatMoney = (amount: number) => money.format(Number.isFinite(amount) ? amount : 0);
const formatDate = (dateValue?: string) => {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  return Number.isNaN(date.getTime()) ? "" : new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short", year: "numeric" }).format(date);
};
const positive = (amount: number) => amount > 0;

const StatusMessage = ({ title, detail }: { title: string; detail: string }) => (
  <main className="billing-receipt-page"><section className="billing-receipt-message" role="alert"><i className="pi pi-file" aria-hidden="true" /><h1>{title}</h1><p>{detail}</p></section></main>
);

const BillingOrderDetails = () => {
  const { invoicenumber } = useParams<{ invoicenumber: string }>();
  const [data, setData] = useState<BillingOrderDetailsData | null>(null);
  const [error, setError] = useState<BillingReceiptError | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const oldTitle = document.title;
    const robots = document.querySelector('meta[name="robots"]') || document.createElement("meta");
    const wasInHead = robots.parentElement !== null;
    robots.setAttribute("name", "robots");
    robots.setAttribute("content", "noindex, nofollow");
    if (!wasInHead) document.head.appendChild(robots);
    document.title = "Farm2Me Receipt";
    return () => { document.title = oldTitle; if (!wasInHead) robots.remove(); };
  }, []);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    if (!invoicenumber || invoicenumber.length > 128 || /[\s/]/.test(invoicenumber)) {
      setError(new BillingReceiptError("not-found"));
      setLoading(false);
      return () => { mounted = false; };
    }
    getBillingOrderDetails(invoicenumber).then((receipt) => { if (mounted) setData(receipt); }).catch((reason) => { if (mounted) setError(reason); }).finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [invoicenumber]);

  const handleDownload = async () => {
    if (!invoicenumber || downloading) return;
    setDownloading(true);
    try {
      const directPdfUrl = `${process.env.REACT_APP_SERVER_ENDPOINT || ""}/api/public/receipt/${encodeURIComponent(invoicenumber)}/invoice`;
      const newWindow = window.open(directPdfUrl, "_blank", "noopener,noreferrer");
      if (newWindow) {
        newWindow.opener = null;
        setDownloading(false);
        return;
      }

      const blob = await downloadBillingInvoice(invoicenumber);
      const pdfBlob = blob && blob.type && !blob.type.toLowerCase().includes("pdf")
        ? new Blob([blob], { type: "application/pdf" })
        : blob;
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoice-${invoicenumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        link.remove();
        URL.revokeObjectURL(url);
      }, 1500);
    } catch { setError(new BillingReceiptError("error")); } finally { setDownloading(false); }
  };

  if (loading) return <main className="billing-receipt-page billing-receipt-loading" aria-busy="true"><ProgressSpinner aria-label="Loading receipt" /><p>Loading receipt</p></main>;
  if (!data || error) {
    if (error?.reason === "expired") return <StatusMessage title="This receipt link has expired" detail="Please ask for a new receipt link." />;
    if (error?.reason === "not-found") return <StatusMessage title="Receipt not found" detail="Check the invoice number and try again." />;
    return <StatusMessage title="Receipt unavailable" detail="Please try again in a little while." />;
  }

  const summary = data.summary;
  const items = expanded ? data.items : data.items.slice(0, 2);
  const payment = data.payment?.displayText || data.payment?.status || data.payment?.mode;

  return (
    <main className="billing-receipt-page">
      <section className="billing-receipt-card" aria-labelledby="billing-receipt-title">
        <header className="billing-receipt-header">
          <div className="billing-receipt-brand">
            <img src={process.env.PUBLIC_URL + "/assests/images/DesiriLogo.png"} alt="Desiri" />
          </div>
          <h1 id="billing-receipt-title">INVOICE</h1>
          <div className="billing-receipt-date"><span>Invoice date</span><strong>{formatDate(data.invoiceDate) || "-"}</strong></div>
        </header>
        <p className="billing-receipt-invoice">#{data.invoiceNumber}</p>
        {data.orderNumber && <p className="billing-receipt-order">Order <strong>#{data.orderNumber}</strong></p>}

        <section className="billing-receipt-details">
          {data.customer?.name && (
            <div className="billing-receipt-customer-block">
              <span>CUSTOMER</span>
              <div className="billing-receipt-customer-value">
                <small>{data.customer.name}</small>
              </div>
            </div>
          )}
          <div className="billing-receipt-store-block">
            <span>STORE</span>
            <div className="billing-receipt-store-value">
              {data.store?.address ? <small>{data.store.address}</small> : <strong>{data.store?.name || "-"}</strong>}
            </div>
          </div>
        </section>
        {(data.legal?.cin || data.legal?.gstin || data.legal?.fssaiLicenseNumber) && <section className="billing-receipt-legal" aria-label="Legal information">{data.legal.cin && <div><span>CIN</span><strong>{data.legal.cin}</strong></div>}{data.legal.gstin && <div><span>GSTIN</span><strong>{data.legal.gstin}</strong></div>}{data.legal.fssaiLicenseNumber && <div><span>FSSAI LICENSE NO.</span><strong>{data.legal.fssaiLicenseNumber}</strong></div>}</section>}
        {payment && <div className="billing-receipt-payment"><i className="pi pi-check-circle" aria-hidden="true" /><span>PAYMENT</span><strong>{payment}</strong></div>}

        <section className="billing-receipt-summary" aria-label="Invoice summary">
          <div><span>ITEMS</span><strong>{summary.itemCount}</strong></div>
          <div><span>TOTAL UNITS</span><strong>{summary.totalUnits}</strong></div>
          <div><span>MRP / TAXABLE AMOUNT</span><strong>{formatMoney(summary.taxableAmount)}</strong></div>
          <div><span>TOTAL DISCOUNTS</span><strong className="billing-receipt-discount">-{formatMoney(summary.discountAmount)}</strong></div>
          {positive(summary.couponDiscountAmount) && <div><span>COUPON DISCOUNT</span><strong className="billing-receipt-discount">-{formatMoney(summary.couponDiscountAmount)}</strong></div>}
          {positive(summary.rewardPointDiscountAmount) && <div><span>REWARD POINTS DISCOUNT</span><strong className="billing-receipt-reward">-{formatMoney(summary.rewardPointDiscountAmount)}</strong></div>}
          {positive(summary.deliveryCharge) && <div><span>DELIVERY CHARGES</span><strong>{formatMoney(summary.deliveryCharge)}</strong></div>}
          {positive(summary.taxAmount) && <div><span>TAX / GST</span><strong>{formatMoney(summary.taxAmount)}</strong></div>}
          {positive(summary.paymentGatewayCharges) && <div><span>PAYMENT GATEWAY CHARGES</span><strong>{formatMoney(summary.paymentGatewayCharges)}</strong></div>}
          <div className="billing-receipt-grand-total"><span>GRAND TOTAL</span><strong>{formatMoney(Math.max(0, summary.grossAmount - summary.rewardPointDiscountAmount))}</strong></div>
          {positive(summary.rewardPointsEarned) && <div className="billing-receipt-earned"><span>REWARD POINTS EARNED</span><strong>{summary.rewardPointsEarned}</strong></div>}
        </section>

        <section className="billing-receipt-items" aria-labelledby="billing-items-title"><div className="billing-receipt-items-heading"><h2 id="billing-items-title">ITEMS</h2><span>{data.items.length}</span></div>{items.map((item, index) => <article className="billing-receipt-item" key={`${item.name}-${index}`}><div className="billing-receipt-item-image">{item.imageUrl ? <img src={item.imageUrl} alt="" /> : <i className="pi pi-shopping-bag" aria-hidden="true" />}</div><div className="billing-receipt-item-name"><strong>{item.name}</strong><small>{item.quantity}{item.unit ? ` ${item.unit}` : ""}</small></div><strong>{formatMoney(item.lineTotal)}</strong></article>)}{data.items.length > 2 && <button className="billing-receipt-view-button" type="button" onClick={() => setExpanded((value) => !value)} aria-expanded={expanded}>{expanded ? "HIDE ITEMS" : "VIEW ALL ITEMS"}<i className={`pi pi-chevron-${expanded ? "up" : "down"}`} aria-hidden="true" /></button>}</section>
        <button className="billing-receipt-download" type="button" onClick={handleDownload} disabled={downloading} aria-label="Download tax invoice"><i className={`pi ${downloading ? "pi-spin pi-spinner" : "pi-download"}`} aria-hidden="true" />{downloading ? "DOWNLOADING..." : "DOWNLOAD TAX INVOICE"}</button>
      </section>
    </main>
  );
};

export default BillingOrderDetails;
